import { useEffect, useRef, useState } from 'react';
import { CONFIG } from '../data/catalog.js';
import { fmt } from '../lib/money.js';
import { useStore } from '../lib/store.jsx';
import { useI18n } from '../lib/i18n.jsx';
import { getOrder, submitSlip } from '../lib/api.js';

const BANK_FALLBACK = { name: 'ธนาคารกสิกรไทย (KBank)', acct: 'xxx-x-xxxxx-x', holder: 'บจก. แคมป์ สตูดิโอ (Barebones)' };

export default function Confirm({ orderNo, nav }) {
  const { lastOrder } = useStore();
  const { t, x } = useI18n();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slipFile, setSlipFile] = useState(null);
  const [slipBusy, setSlipBusy] = useState(false);
  const [slipResult, setSlipResult] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const lo = lastOrder && lastOrder.order_no === orderNo ? lastOrder : null;

  useEffect(() => {
    let on = true;
    setLoading(true);
    getOrder(orderNo)
      .then((o) => { if (on) setOrder(o); })
      .catch(() => {})
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [orderNo]);

  // Poll สถานะทุก 5 วินาทีเมื่อยังไม่ paid
  useEffect(() => {
    if (!order || order.status === 'paid' || order.status === 'shipped') return;
    pollRef.current = setInterval(() => {
      getOrder(orderNo)
        .then((o) => { if (o?.status) setOrder(o); })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [order?.status, orderNo]);

  const handleSlip = async () => {
    if (!slipFile) return;
    setSlipBusy(true);
    try {
      const res = await submitSlip(orderNo, slipFile);
      setSlipResult(res);
      if (res.order_status === 'paid') {
        setOrder((o) => o ? { ...o, status: 'paid' } : o);
      }
    } catch {
      setSlipResult({ verify_state: 'error', message: t('conf.slipFail') });
    } finally {
      setSlipBusy(false);
    }
  };

  const isPaid = order?.status === 'paid' || order?.status === 'shipped';
  const origMethod = order?.pay_method || lo?.payMethod || 'transfer';
  const [payView, setPayView] = useState(origMethod);
  const isTransfer = payView === 'transfer';
  const isStripe = payView === 'stripe';
  const grand = order?.grand_total || lo?.grand;
  const bank = order?.bank || BANK_FALLBACK;

  // Loading state
  if (loading && !lo) {
    return (
      <section className="blk">
        <div className="wrap"><div className="conf"><div className="conf-spin" /><p>{t('conf.loading')}</p></div></div>
      </section>
    );
  }

  // Order not found
  if (!loading && !order && !lo) {
    return (
      <section className="blk">
        <div className="wrap"><div className="conf">
          <h2>{t('conf.notFound')}</h2>
          <p className="txt">{t('conf.notFoundNo', orderNo)}</p>
          <button className="btn" style={{ marginTop: 20 }} onClick={() => nav('/')}>{t('conf.home')}</button>
        </div></div>
      </section>
    );
  }

  return (
    <section className="blk">
      <div className="wrap">
        <div className="conf">
          {/* Header */}
          {isPaid ? (
            <>
              <div className="chk"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l6 6L20 6" /></svg></div>
              <h2>{t('conf.paid')}</h2>
            </>
          ) : (
            <>
              <div className="chk chk-pending"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M12 6v6l4 2" /><circle cx="12" cy="12" r="9" /></svg></div>
              <h2>{isTransfer ? t('conf.pay') : t('conf.waiting')}</h2>
            </>
          )}

          <div className="ordno">{t('conf.order', orderNo)}</div>

          {lo?.simulated && (
            <div className="note" style={{ textAlign: 'left', margin: '0 auto 12px', maxWidth: 460 }}>
              {t('conf.test')}
            </div>
          )}

          {/* PAID / SHIPPED state */}
          {isPaid && (
            <>
              {order?.tracking_no && (
                <div style={{ fontFamily: 'var(--mono)', fontSize: 13, margin: '12px 0', color: 'var(--ink-2)' }}>
                  {t('conf.trackingNo')} <b style={{ color: 'var(--ink)', letterSpacing: '.06em' }}>{order.tracking_no}</b>
                </div>
              )}
              <p className="txt">
                {t('conf.paidBody1')}<br />
                {t('conf.paidBody2')}<br />
                <a href="/track" onClick={(e) => { e.preventDefault(); nav('/track'); }} style={{ color: 'var(--slate-deep)' }}>{t('conf.trackLink')}</a>
                {' · '}{t('conf.contact')} <b>{CONFIG.email}</b>
              </p>
            </>
          )}

          {/* Payment method switcher */}
          {!isPaid && (
            <div style={{ maxWidth: 420, margin: '0 auto 18px', display: 'flex', gap: 8 }}>
              <button className={`btn sm${isTransfer ? '' : ' ghost'}`} style={{ flex: 1 }} onClick={() => setPayView('transfer')}>{t('co.transfer')}</button>
              <button className={`btn sm${isStripe ? '' : ' ghost'}`} style={{ flex: 1 }} onClick={() => setPayView('stripe')}>{t('co.card')}</button>
            </div>
          )}

          {/* TRANSFER — awaiting payment */}
          {!isPaid && isTransfer && (
            <div className="conf-pay">
              <div className="bankbox" style={{ maxWidth: 420, margin: '0 auto' }}>
                <div className="k">{t('conf.bank')}</div>{x(bank.name)}<br />
                <div className="k">{t('conf.acct')}</div><span className="acct-num">{bank.acct}</span><br />
                <div className="k">{t('conf.holder')}</div>{bank.holder}<br />
                <div className="k">{t('conf.amount')}</div><b>{grand ? fmt(grand) : '…'}</b>
              </div>

              {/* Slip upload */}
              {!slipResult?.verify_state || slipResult.verify_state === 'error' ? (
                <div style={{ maxWidth: 420, margin: '16px auto 0' }}>
                  <div
                    className={`upload${slipFile ? ' filled' : ''}`}
                    onClick={() => fileRef.current?.click()}
                  >
                    {slipFile ? t('conf.slipReady') : t('conf.slipTap')}
                    <div className="sub">{slipFile ? slipFile.name : t('conf.slipTypes')}</div>
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => { setSlipFile(e.target.files?.[0] || null); setSlipResult(null); }} />
                  {slipFile && (
                    <button className="btn full" style={{ marginTop: 12, padding: 14 }} onClick={handleSlip} disabled={slipBusy}>
                      {slipBusy ? t('conf.slipChecking') : t('conf.slipSend')}
                    </button>
                  )}
                  {slipResult?.verify_state === 'error' && (
                    <div className="slip-msg slip-err">{slipResult.message}</div>
                  )}
                  <div className="slipok" style={{ justifyContent: 'center', marginTop: 14 }}>{t('conf.slipAuto')}</div>
                </div>
              ) : (
                <div style={{ maxWidth: 420, margin: '16px auto 0' }}>
                  <div className={`slip-msg ${slipResult.verify_state === 'verified' ? 'slip-ok' : 'slip-warn'}`}>
                    {slipResult.message}
                  </div>
                  {slipResult.verify_state !== 'verified' && (
                    <button className="btn ghost sm" style={{ marginTop: 10 }}
                      onClick={() => { setSlipResult(null); setSlipFile(null); }}>
                      {t('conf.slipRetry')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STRIPE — pay or waiting */}
          {!isPaid && isStripe && (
            <div className="conf-pay">
              <p className="txt" style={{ marginBottom: 14 }}>{t('conf.stripeLede', grand ? ` ${fmt(grand)}` : '')}</p>
              <a className="btn full" href={`/api/pay/stripe?order=${encodeURIComponent(orderNo)}`} style={{ display: 'block', textAlign: 'center', padding: 16, textDecoration: 'none' }}>
                {t('conf.payCard')}
              </a>
              <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 10, textAlign: 'center' }}>{t('conf.stripeSecure')}</div>
            </div>
          )}

          <div style={{ marginTop: 28, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => nav('/track')}>{t('conf.track')}</button>
            <button className="btn ghost" onClick={() => nav('/')}>{t('conf.home')}</button>
          </div>
          {!isPaid && <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 12, textAlign: 'center' }}>{t('conf.comeback', orderNo)}</p>}
        </div>
      </div>
    </section>
  );
}
